import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from "react-router-dom";
import Login from "./Login";
import SignUp from "./SignUp";
import Profile from "./Profile";
import EditProfile from "./EditProfile"; // Import EditProfile
import Home from "./Home"; // Import Home
import AdminDashboard from "./AdminDashboard"; // Import AdminDashboard
import CreateBenefit from "./CreateBenefit"; // Import CreateBenefit
import PublicProfile from "./PublicProfile"; // Import PublicProfile
import Messages from "./Messages"; // Import Messages
import MessageThread from "./MessageThread"; // Import MessageThread
import AuthSuccess from "./AuthSuccess"; // Import AuthSuccess for OAuth redirects
import ProtectedRoute from "./ProtectedRoute"; // Import the ProtectedRoute component
import axios from "axios";
import { API_BASE_URL } from "./config";

const NavBar = () => {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [followRequestCount, setFollowRequestCount] = React.useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);

  React.useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
    
    // Check admin status and follow requests
    if (token) {
      checkAdminStatus();
      fetchFollowRequestCount();
      fetchUnreadMessageCount();
    }
  }, [location]);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(res.data.isAdmin);
    } catch (err) {
      setIsAdmin(false);
    }
  };

  const fetchFollowRequestCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/follow-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowRequestCount(res.data.received?.length || 0);
    } catch (err) {
      console.error("Error fetching follow requests:", err);
    }
  };

  const fetchUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessageCount(res.data.count || 0);
    } catch (err) {
      console.error("Error fetching unread messages:", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    setIsAdmin(false);
  };

  const closeNavbar = () => {
    // Close the navbar on mobile after clicking a link
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
      const bsCollapse = new window.bootstrap.Collapse(navbarCollapse, {
        toggle: false
      });
      bsCollapse.hide();
    }
  };

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-light">
      <div className="container">
        <Link className="navbar-brand" to="/" onClick={closeNavbar}>
          <img src="/black.png" alt="Senior Syndicate" height="40" />
        </Link>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            {isLoggedIn ? (
              <>
                <li className="nav-item">
                  <Link to="/home" className="nav-link" onClick={closeNavbar}>Home</Link>
                </li>
                <li className="nav-item">
                  <Link to="/messages" className="nav-link position-relative" onClick={closeNavbar}>
                    Messages
                    {unreadMessageCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {unreadMessageCount}
                        <span className="visually-hidden">unread messages</span>
                      </span>
                    )}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link to="/profile" className="nav-link position-relative" onClick={closeNavbar}>
                    Profile
                    {followRequestCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {followRequestCount}
                        <span className="visually-hidden">follow requests</span>
                      </span>
                    )}
                  </Link>
                </li>
                {isAdmin && (
                  <li className="nav-item">
                    <Link to="/admin" className="nav-link text-success" onClick={closeNavbar}>Admin Dashboard</Link>
                  </li>
                )}
                <li className="nav-item">
                  <Link to="/login" className="nav-link" onClick={(e) => { handleLogout(); closeNavbar(); }}>Logout</Link>
                </li>
              </>
            ) : (
              <>
                <li className="nav-item">
                  <Link to="/login" className="nav-link" onClick={closeNavbar}>Login</Link>
                </li>
                <li className="nav-item">
                  <Link to="/signup" className="nav-link" onClick={closeNavbar}>Sign Up</Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

const App = () => {
  return (
    <Router>
      <NavBar /> {/* Include NavBar in the app */}
      <Routes>
        {/* Redirect root path to login page */}
        <Route path="/" element={<Navigate to="/login" />} />

        {/* Login and SignUp routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/auth-success" element={<AuthSuccess />} /> {/* OAuth success redirect */}

        {/* Protected routes */}
        <Route path="/home" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} /> {/* Add edit-profile route */}
        
        {/* Messaging routes */}
        <Route path="/messages" element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        } />
        <Route path="/messages/:userId" element={
          <ProtectedRoute>
            <MessageThread />
          </ProtectedRoute>
        } />
        
        {/* Public user profile route */}
        <Route path="/user/:username" element={<PublicProfile />} />
        
        {/* Admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/create-benefit" element={
          <ProtectedRoute>
            <CreateBenefit />
          </ProtectedRoute>
        } />
        
        {/* Username-based profile route - must be last to avoid conflicts */}
        <Route path="/:username" element={<PublicProfile />} />
      </Routes>
    </Router>
  );
};

export default App;
